import React, { useState} from 'react';
import { Grid } from '../../../shared/grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Autocomplete, Button, FormControl, FormControlLabel, Radio, RadioGroup, TextField, Tooltip, Typography } from '@mui/material';
import Chip from '@mui/material/Chip';
import { weatherSelectDownload, hours, weatherFileFormatHourly, weatherFileFormatDaily, } from '../../../constants';
import CloseIcon from '@mui/icons-material/Close';
import '../Download.scss';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment-timezone';
import { isValidDate } from 'src/store/common';
import { useFormikContext } from 'formik';
import InfoIcon from '@mui/icons-material/Info';
import dayjs, { Dayjs } from 'dayjs';
import { VirtualizedListBox } from '../FireDanger';
type ScrollCtxValue ={
    scrollTopRef: React.MutableRefObject<number>;
    restoreToken: number;
};

const ScrollCtx = React.createContext<ScrollCtxValue | null>(null);

type WeatherDLProps = {
    weatherDLParams: any,
    clearSelected: any,
    onPresetSelected: (presetSelected: boolean) => void ,
}


export const Weather: React.FC<WeatherDLProps> = ({ weatherDLParams, clearSelected, onPresetSelected }) => {
    const { values, errors, touched, setFieldTouched, setFieldValue } = useFormikContext<any>();
    const [errorStartDate, setErrorStartDate] = useState<string | null>(null);
    const [errorEndDate, setErrorEndDate] = useState<string | null>(null);
    const [presetSelected, setPresetSelected] = useState(false); 

    const [selectAllChecked, setSelectAllChecked] = useState(false);
    const [filteredStations, setFilteredStations] = useState<any[]>([]);
    const [inputValue, setInputValue] = useState('');
    const scrollTopRef = React.useRef(0);
    const [restoreToken, setRestoreToken] = useState(0);

    const stylebutton = presetSelected ? { backgroundColor: '#012954' }:{};

    // helper validators (return error string or null)
    const validateStart = (start: Dayjs | null, end: Dayjs | null, vals: any) => {
        if (!start) return "Start date is required";
        // forecast requires start >= today
        if (vals.dataSet === "forecast" && start.isBefore(weatherDLParams.today(), "day")) {
         return "Start date cannot be in the past for forecasts";
        }
        // if end exists, ensure start < end
        if (end && !start.isBefore(end, "day") && !start.isSame(end, "day")) {
            return "Start date must be before end date";
        }
        return null;
    };

    const validateEnd = (end: Dayjs | null, start: Dayjs | null, vals: any) => {
        if (!end) return "End date is required";
        if (start && !end.isAfter(start, "day") && !end.isSame(start, "day")) {
            return "End date must be after start date";
        }
        if ((vals.dataSet === "forecast") && vals.dateTimeIncrements === "daily") {
            if (start && end.isBefore(start, "day")) {
                return "Forecast unavailable for days in the past";
            }
        }
        return null;
    };

    const onStartChange = (newValue:Date|null) => {
        const currentValues = values;
        const newStart = newValue ? dayjs(newValue).startOf('day') : null;
        const currentEnd = dayjs(moment(values.endDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).utc().toDate());

        const startError = validateStart(newStart, currentEnd, currentValues);
        const endError = validateEnd(currentEnd, newStart, currentValues);

        setFieldValue('startDate', moment(moment(newStart?.toDate()).utc().toDate()).format("YYYY-MM-DD"));
        if(startError) {
            setErrorStartDate(startError);
            weatherDLParams.setErrorDate('Invalid Date');
        }
        else {
            setFieldTouched("startDate", true);
            setErrorStartDate('');
            weatherDLParams.setErrorDate('');
        }

        if(endError) {
            setErrorEndDate(endError);
            weatherDLParams.setErrorDate('Invalid Date');
        }
        else {
            setErrorEndDate('');
            weatherDLParams.setErrorDate('');
        }
    }
    const onEndChange = (newValue:Date|null) => {
        const currentValues = values;
        const newEnd = newValue ? dayjs(newValue).startOf("day") : null;
        const currentStart = dayjs(moment(values.startDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).utc().toDate());

        const endError = validateEnd(newEnd, currentStart, currentValues);
        const startError = validateStart(currentStart, newEnd, currentValues);

        setFieldValue("endDate", moment(moment(newEnd?.toDate()).utc().toDate()).format("YYYY-MM-DD"));
        if (endError) {
            setErrorEndDate(endError);
            weatherDLParams.setErrorDate('Invalid Date')
        }
        else {
            setFieldTouched("endDate", true);
            setErrorEndDate('');
            weatherDLParams.setErrorDate('');
        }

        if (startError) {
            setErrorStartDate(startError);
            weatherDLParams.setErrorDate('Invalid Date');
        }
        else {
            setErrorStartDate('');
            weatherDLParams.setErrorDate('');
        }
    };

    const handleTimeIncrements = (e:any)=>{
        weatherDLParams.setDataIncrements(e);
    }

    return (
        <div>
            {/* WEATHER SELECTIION */}
            <Grid row desktop={{ col: 6 }} className={values.dataSubject === 'weather' ? '' : 'hideRow'}>
                    <label id='dataSet'>Data Set <span className='required'>Required</span>
                        <Select
                            displayEmpty
                            renderValue={values.dataSet !== "" ? undefined : () => "Select a Data Set"}
                            labelId='dataSet'
                            name="dataSet"
                            className="dataDownload"
                            onBlur={() => {
                                setFieldTouched('dataSet');
                                weatherDLParams.setLinkGenerated(false);
                            }}
                            onChange={(e) => { 
                                weatherDLParams.setDataSet(e); 
                                setPresetSelected(false); 
                                onPresetSelected(false); 
                            }}
                            label="Select a Data Set"
                            value={values.dataSet}
                            aria-disabled={values.dataSubject === ''}
                            error={!!errors.dataSet && !!touched.dataSet}
                            placeholder={"Select a Data Set"}
                        >
                            {
                                weatherSelectDownload.map((weatherOption: any) => (
                                    <MenuItem value={weatherOption.value} key={weatherOption.value}>{weatherOption.label}</MenuItem>
                                ))
                            }
                        </Select>
                    </label>
                    <span style={{ color: '#d32f2f' }}>{touched.dataSet && errors.dataSet}</span>
            </Grid>
            <Grid row className={(values.dataSubject === ('weather') ? '' : 'hideRow')} desktop={{ col: 6 }}>
                <label htmlFor = 'DateTimeFrequency'>Date Time Frequency <span className='required'>Required</span>
                    <div style={{ margin: '5px 0', fontSize: 'medium' }}>
                        <div className={"font-sans-sm "} style={{ paddingTop: '10px', paddingBottom: '10px', fontWeight: 'normal' }}>
                        <FormControl>
                            <RadioGroup
                                id="weatherTimeIncrements" 
                                name="controlled-radio-buttons-group"
                                value={values.dataTimeIncrements}
                                onChange={(event) => { handleTimeIncrements(event); }}
                                onBlur={weatherDLParams.handleBlur}
                            >
                                <FormControlLabel 
                                    key={`daily`} 
                                    value={'daily'} 
                                    control={<Radio size='small' style={{padding: '5px'}}/>} 
                                        label={<Typography style={{fontSize: '1rem'}}>Daily</Typography>} 
                                />
                                <FormControlLabel 
                                    key={`hourly`} 
                                    value={'hourly'} 
                                    control={<Radio size='small' style={{padding: '5px'}}/>} 
                                        label={
                                            <Typography style={{fontSize: '1rem'}}>
                                                Hourly
                                                <Tooltip title="Due to restrictions in place by the network owner, no hourly weather data is available for restricted networks.">
                                                    <InfoIcon fontSize='small' />
                                                </Tooltip>
                                            </Typography>
                                        } 
                                />
                            </RadioGroup>
                        </FormControl>
                        </div>

                    </div>
                </label>
                <span style={{ color: '#d32f2f' }}>{errors.dataTimeIncrements && touched.dataTimeIncrements && errors.dataTimeIncrements}</span>
                {values.dataTimeIncrements === 'daily' && (
                    <div style={{ paddingLeft: '1.5%', fontSize: '16px', width: '100%', paddingBottom: '1.5%' }} className="selectedStationTextBox">
                        (Daily) Maximum/Minimum data will be downloaded from midnight to midnight local station time.
                    </div>
                )}
            </Grid>
            <Grid row desktop={{ col: 6 }}>
                <label id='dataFormat'>Data Format <span className='required'>Required</span>
                    <Select
                        displayEmpty
                        renderValue={values.dataFormat !== "" ? undefined : () => "Select a Data Format"}
                        labelId='dataFormat'
                        name="dataFormat"
                        onBlur={() => {setFieldTouched('dataFormat'); weatherDLParams.setLinkGenerated(false);}}
                        error={!!errors.dataFormat && !!touched.dataFormat}
                        className="dataDownload"
                        onChange={weatherDLParams.setDataFormat}
                        label="Select Data Format"
                        value={values.dataFormat}
                        aria-disabled={values.dataSubject === '' || values.dataSet === '' || values.dataTimeIncrements===''}
                    >
                        {
                            values.dataTimeIncrements ==='hourly' && weatherFileFormatHourly.map((fileType: any) => (
                                <MenuItem value={fileType.value} key={fileType.value}>{fileType.label}</MenuItem>
                            ))
                        }
                        {
                            values.dataTimeIncrements ==='daily' && weatherFileFormatDaily.map((fileType: any) => (
                                <MenuItem value={fileType.value} key={fileType.value}>{fileType.label}</MenuItem>
                            ))
                        }
                    </Select>
                </label>

                <span style={{ color: '#d32f2f' }}>{errors.dataFormat && touched.dataFormat && errors.dataFormat}</span>
                {values.dataSubject.includes('weather') && values.timeZoneType === 'utc' && values.dataFormat!=='fw21' && (
                    <div style={{ paddingLeft: '1.5%', fontSize: '16px', width: '100%', paddingBottom: '1.5%' }} className="selectedStationTextBox">
                    Time is in UTC
                    </div>
                )}
                {values.dataSubject.includes('weather') && (values.timeZoneType === 'lst' || values.dataFormat==='fw21') && (
                    <div style={{ paddingLeft: '1.5%', fontSize: '15px', width: '100%', paddingBottom: '1.5%' }} className="selectedStationTextBox">
                    Time is adjusted to time zone of station but not adjusted for daylight savings.
                    </div>
                )}
            </Grid>
            <Grid row className={values.dataSubject === ('weather') ? '' : 'hideRow'} desktop={{ col: 8 }}>
                <div><span className='font-sans-md text-bold'>Date Range</span> <span className='required1'>Required</span>
                    <div className={`date-range-picker-wrapper ${weatherDLParams.errorDate ? 'error' : ''}`} style={{ margin: '10px 0' }}>
                        <DatePicker
                            label="Start Date"
                            disabled={presetSelected} 
                            disablePast={values.dataTimeIncrements==='daily' && values.dataSet==='forecast' && values.dataSubject==='weather'}
                            value={dayjs(values.startDate).startOf("day").toDate()}
                            onChange={onStartChange}
                            onError={(reason, value) => {
                                if (reason === 'invalidDate' || value === null || !isValidDate(value)) {
                                    setErrorStartDate('Invalid Start Date');
                                    weatherDLParams.setErrorDate('Invalid Date');
                                }
                                else if(reason === 'disablePast') {
                                    setErrorStartDate('Daily Forecast unavailable for previous dates');
                                    weatherDLParams.setErrorDate('Invalid Date');
                                }
                                else if(reason){
                                    setErrorStartDate('An Error on Start Date: ' + reason);
                                    weatherDLParams.setErrorDate('Invalid Date');
                                }
                                else{
                                    setErrorStartDate('');
                                    if(errorEndDate === '') {
                                        weatherDLParams.setErrorDate('');
                                    }
                                }
                            }}
                            slotProps={{
                                textField: {
                                    error: errorStartDate ? true : false,
                                    helperText: errorStartDate,
                                    id: 'uploadStartDate',
                                },

                            }}
                        />
                        <span style={{ margin: '0 10px' }}></span>
                        <DatePicker
                            label="End Date"
                            disabled={presetSelected} 
                            disablePast={values.dataTimeIncrements==='daily' && values.dataSet==='forecast' && values.dataSubject==='weather'}
                            value={dayjs(values.endDate).startOf("day").toDate()}
                            aria-labelledby='uploadEndDate'

                            onChange={onEndChange}
                            onError={(reason, value) => {
                                if (reason === 'invalidDate' || value === null || !isValidDate(value)) {
                                    setErrorEndDate('Invalid End Date');
                                    weatherDLParams.setErrorDate('Invalid Date');
                                }
                                else if(reason === 'disablePast') {
                                    setErrorEndDate('Daily Forecast unavailable for previous dates');
                                    weatherDLParams.setErrorDate('Invalid Date');
                                }
                                else if(reason){
                                    setErrorEndDate('An Error on End Date: ' + reason);
                                    weatherDLParams.setErrorDate('Invalid Date');
                                }
                                else{
                                    setErrorEndDate('');
                                    if(errorStartDate === '') {
                                        weatherDLParams.setErrorDate('');
                                    }
                                }
                            }}
                            slotProps={{
                                textField: {
                                    error: errorEndDate ? true : false,
                                    helperText: errorEndDate,
                                    id: 'uploadEndDate',

                                },
                            }}
                        />
                    </div>
                </div>
            </Grid>
            <span style={{ color: '#d32f2f' }}>{weatherDLParams.errorDate}</span>
            {
                (values.dataTimeIncrements === 'hourly') &&
                <Grid row className={!values.dataTimeIncrements.includes('hourly') || values.dataSubject !== 'weather' ? 'hideRow' : ''} gap="md">
                    <Grid desktop={{ col: 2 }}>
                        <i className="fas fa-clock" />
                        <label id='startHr'>
                            <Tooltip title="This is the hour that your observations will begin">
                                <span>Start Hour <InfoIcon /></span>
                            </Tooltip>
                            <Select
                                disabled={presetSelected}
                                displayEmpty
                                onBlur={weatherDLParams.handleBlur}
                                renderValue={values.startHour !== "" ? undefined : () => "00:00"}
                                labelId='startHour'
                                name="startHour"
                                onChange={weatherDLParams.setStartHour}
                                label="startHour"
                                value={values.startHour}
                                className="select-styles special dataDownload"
                                placeholder='00:00'
                                id="startHour"
                            >
                                {hours.map((hr: any) => (
                                    <MenuItem value={hr.value} key={`starthour - ${hr.value}`}>{hr.label}</MenuItem>
                                ))}
                            </Select>
                        </label>
                    </Grid>
                    <Grid desktop={{ col: 2 }}>
                        <i className="fas fa-clock" />

                        <label id='endHr'>
                            <Tooltip title="This is the hour that your observations will end">
                                <span>End Hour <InfoIcon /></span>
                            </Tooltip>
                            <Select
                            disabled={presetSelected}
                                displayEmpty
                                onBlur={weatherDLParams.handleBlur}
                                renderValue={values.endHour !== "" ? undefined : () => "23:00"}
                                labelId='endHour'
                                name="endHour"
                                onChange={weatherDLParams.setEndHour}
                                label="Select EndHour"
                                value={values.endHour}
                                className="select-styles special dataDownload"
                                placeholder='23:00'
                                id="endHour"
                            >
                                {hours.map((hr: any) => (
                                    <MenuItem value={hr.value} key={`endhour - ${hr.value}`}>{hr.label}</MenuItem>
                                ))}
                            </Select>
                        </label>
                    </Grid>
                </Grid>
            }
            <Grid row className={(!values.dataFormat.includes("csv") || values.dataSubject !== 'weather') || !values.dataTimeIncrements.includes("hourly") ? 'hideRow' : ''} style={{ paddingBottom: '15px' }}>
                <span style={{ fontStyle: "italic" }}> Forecast data may be incomplete on day seven</span>
            </Grid>
            {/* CONDITIONAL BUTTON */}
            {values.dataSet === "observationforecast" && (
                <Grid row desktop={{ col: 6 }} style={{ marginTop: "15px", marginBottom: "15px" }}>
                <Button
                    variant="contained"
                    color="primary"
                    style={stylebutton}
                    onClick={() => {
                        weatherDLParams.setLinkGenerated(false);
                        setPresetSelected((prev) => !prev); 
                        const isPresetSelected = !presetSelected;
                        onPresetSelected(isPresetSelected);
                        const startDate = moment().subtract(5, 'days').utc().format('YYYY-MM-DD');
                        setFieldValue('startDate', startDate);
                        if (presetSelected) {
                            // Apply preset logic
                            const endDate = moment().add(7, 'days').utc().format('YYYY-MM-DD');
                            setFieldValue('endDate', endDate);
                        }else{
                            const endDate = moment().add(6, 'days').utc().format('YYYY-MM-DD');
                            setFieldValue('endDate', endDate);
                        }

                        }}
                >
                    Previous 5 Days and 7-Day Forecast
                </Button>
                </Grid>
            )}

            <Grid row className={values.dataSubject === '' ? 'hideRow' : ''} desktop={{ col: 6 }}>
                <div style={{width: '100%'}}><label className='font-sans-md text-bold' htmlFor='selectedStations'>Selected Station(s)<span className='required1'>Required</span>
                    <div className='searchStationsDownloadPage'>
                        <i className="fas fa-search" />
                        <ScrollCtx.Provider value ={{scrollTopRef, restoreToken}}>

                            <Autocomplete
                                disableCloseOnSelect
                                multiple
                                noOptionsText={
                                    <div className='no-results'>
                                        <div>No results match your search criteria.</div>
                                        <div><b>Here are a few tips:</b></div>
                                        <div>• Remember to check your spelling<br />
                                            • Ensure you are searching either a Station Name or Station ID number</div>
                                    </div>
                                }
                                className="dataDownload"
                                inputValue={inputValue}
                                fullWidth
                                onInputChange={(event, newInputValue, reason) => {
                                if (reason === 'input') {
                                    setInputValue(newInputValue);
                            
                                    const search = newInputValue.toLowerCase();
                                    const filtered = weatherDLParams?.stationsForSearch.filter((station:any) => {
                                    if (search.length <= 2 && search.length !== 0) {
                                        return station.state?.toLowerCase().includes(search);
                                    }
                                    return (
                                        station.station_name?.toLowerCase().includes(search) ||
                                        station.network_name?.toLowerCase().includes(search) ||
                                        station.state?.toLowerCase().includes(search)
                                    );
                                    });
                            
                                    if(weatherDLParams?.currSelectedStations?.length>0){
                                        setFilteredStations([...weatherDLParams.currSelectedStations, ...filtered]);
                                    }
                                    else{
                                        setFilteredStations(filtered);
                                    }                                }
                                }}
                                onChange={(e, value) => {
                                    const isSelectAllCurrentlyChecked = selectAllChecked;
                                    const isSelectAllStillSelected = value.some((item) => item.value === 'select-all');
                                    const isFiltered = inputValue.trim().length > 0;                             
                                    const stationsToSelect = isFiltered ? filteredStations : weatherDLParams?.stationsForSearch;
                                    if (isSelectAllCurrentlyChecked && !isSelectAllStillSelected) {
                                    weatherDLParams.handleSearch(e, []);
                                    setSelectAllChecked(false);
                                    return;
                                    }
                                    if (!isSelectAllCurrentlyChecked && isSelectAllStillSelected) {
                                    const allWithSelectAll = [...stationsToSelect];
                                    if (!isFiltered) {
                                        allWithSelectAll.unshift({
                                        station_name: 'Select All',
                                        network_name: 'Select All',
                                        value: 'select-all',
                                        search_name: 'Select All',
                                        });
                                    }
                                    weatherDLParams.handleSearch(e, allWithSelectAll);
                                    setSelectAllChecked(!isFiltered);
                                    setInputValue('');
                                    setFilteredStations(weatherDLParams?.stationsForSearch);
                                    return;
                                    }
                                    if (isSelectAllCurrentlyChecked && isSelectAllStillSelected) {
                                    weatherDLParams.handleSearch(e, value);
                                    setSelectAllChecked(false);
                                    return;
                                    }
                                    weatherDLParams.handleSearch(e, value);                           
                                    const allSelected = value.length === weatherDLParams?.stationsForSearch.length;
                                    const noFilter = inputValue.trim().length === 0;
                                    setSelectAllChecked(allSelected && noFilter);
                                    setRestoreToken((t)=> t+1)
                                }}
                                value={weatherDLParams.valsFromSearch}
                                id="selectedStations"
                                onBlur={() => setFieldTouched('dataSelectedStations')}
                                options={weatherDLParams?.stationsForSearch? [{station_name:'Select All', network_name: 'Select All', value: 'select-all',search_name: 'Select All', type: `${values.dataTimeIncrements}`, download:`weather`},...weatherDLParams.stationsForSearch]:[]}
                                renderOption={
                                    (props, option)=>(
                                    <li {...props} key={option.search_name} value={option} id={option}>
                                        {option.search_name}
                                    </li>
                                )}
                                isOptionEqualToValue={(option,value)=>option?.search_name===value?.search_name}
                                getOptionLabel={(option)=>`${option.search_name}`}
                                filterOptions={(options, {inputValue})=>{
                                    const search = inputValue.toLowerCase();
                                    if(search.length<=2 && search.length!==0){
                                        return options.filter((option)=>
                                            option.state?.toLowerCase().includes(search) || 
                                            option.station_name==='Select All' ||
                                            option.station_id.toString().includes(search)
                                        )
                                    }
                                    return options.filter((option)=>
                                        option.station_name?.toLowerCase().includes(search) ||
                                        option.network_name?.toLowerCase().includes(search) ||
                                        option.state?.toLowerCase().includes(search) ||
                                        option.station_name==='Select All' ||
                                        option.station_id.toString().includes(search)

                                    )
                                }}   
                                ListboxComponent={VirtualizedListBox}               
                                renderInput={(params) => (
                                    <TextField
                                        placeholder='Search All Stations...'
                                        className="dataDownload"
                                        aria-label={'Search Station...'}
                                        data-testid={'search-bar'}
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            type: 'search',
                                        }}
                                        helperText={!!touched.dataSelectedStations && !!errors.dataSelectedStations ? errors.dataSelectedStations : ''}
                                        fullWidth
                                        FormHelperTextProps={{
                                            tabIndex: 0
                                        }}
                                        aria-errormessage='Station(s) are required'
                                        onBlur={() => setFieldTouched('dataSelectedStations')}
                                        error={!!touched.dataSelectedStations && !!errors.dataSelectedStations}
                                    />
                                )}
                            />
                        </ScrollCtx.Provider>
                    </div>
                </label>
                    {
                        values.dataTimeIncrements ==='hourly' && values.dataFormat!=='zip' && <div className={'selectedStationTextBox'} style={{ marginTop: '10%' }}>
                        <ul>
                            <li>Up to 300 stations can be selected for requests that are greater than or equal to 20-years worth of data</li>
                        </ul>
                    </div>
                    }
                    {
                        values.dataTimeIncrements ==='hourly' && values.dataFormat==='zip' && <div className={'selectedStationTextBox'} style={{ marginTop: '10%' }}>
                        <ul>
                            <li>Up to 50 stations can be selected for requests that are greater than or equal to 20-years worth of data</li>
                        </ul>
                    </div>
                    }
                </div>

                {
                      
                      selectAllChecked ? (
                        <div className={values.dataSelectedStations ? 'selectedStations' : 'hideRow'}>
                            <div> {`All stations have been selected. Total stations selected: ${weatherDLParams.currSelectedStations?.length-1}`}</div>
                            <div><button type='button' onClick={weatherDLParams.clearSelected}>Clear All</button></div>

                        </div>
                        ): (
                          <div className={values.dataSelectedStations ? 'selectedStations' : 'hideRow'}>
                          <div>{`These are your selected stations. Total stations selected: ${weatherDLParams.currSelectedStations?.length}`}</div>
                          <div className="smooth-scrollbar" style={{maxHeight: '400px', border: '1px solid #717171', width: '75vw',padding: '3px', borderRadius:'5px'}}>
                              {
                            weatherDLParams.currSelectedStations.map((station) =>
                                <Chip 
                                    label={`${station.station_name}, ${station.network_name}, ${station.state}, ${station.station_id}`}
                                    key={station.station_id} 
                                    onDelete={() => weatherDLParams.handleDelete(station.station_id)} 
                                    onKeyDown={(event)=>{event.key==="Backspace" && weatherDLParams.handleDelete(station.station_id) }}
                                    variant="outlined" 
                                    deleteIcon={<CloseIcon tabIndex={0} onKeyDown={(event) => { event.key==='Enter' && weatherDLParams.handleDelete(station.station_id)}}/>} 
                                />
                                        )

                                    }
                                </div>
                                <div><button type='button' onClick={clearSelected}>Clear All</button></div>
                            </div>  
                            )
                        }
            </Grid>

        </div>
    )
}
